import {
    Box,
    Button,
    FormControl,
    FormLabel,
    HStack,
    IconButton,
    Input,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { MdRefresh } from 'react-icons/md';
import PageCard from '../../components/page-card';
import InlineErrorState from '../../components/states/InlineErrorState';
import { translate } from '../../i18n';
import ScopePrompt from '../_shared/ScopePrompt';
import { READ_FAILED_I18N_KEY, classifyRead } from '../_shared/readState';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import {
    BatchUpdateAccessCodesInput,
    useBatchUpdateAccessCodes,
    useGetAccessCodes,
} from './hooks/useAccessCodes';

type DraftRow = { courtId: number; code: string };

export default function AccessCodesPage() {
    const { selectedOrganization } = useSelectedOrganization();
    const orgId = selectedOrganization?.id;
    const featureEnabled = !!selectedOrganization?.gameBookingCodeEnabled;

    const toast = useToast();
    const labelColor = useColorModeValue('secondaryGray.900', 'white');

    const { data, isError, isInitialLoading } = useGetAccessCodes(orgId, featureEnabled);
    // The defect this replaces was in the ANSWERED-looking branch, not in the
    // spinner. The page took no error flag at all, so a refused
    // `GET organizations/:id/access-codes` left `data` undefined, the hydration
    // effect below returned early, `rows` stayed `[]` — and the table fell
    // through to `access_codes.no_courts`: "No courts in this organization
    // yet.", stated about a question the API declined to answer, on the page
    // that configures the door code players are given for their game.
    //
    // `classifyRead`'s `unreadable` requires `!data`, so a BACKGROUND refetch
    // that fails over codes already on screen keeps them (R96-3) — the mirror
    // defect, and the one run 114 found on the door PINs one feature over.
    const readState = classifyRead(isError, isInitialLoading, data);
    const { mutateAsync: save, isLoading: isSaving } = useBatchUpdateAccessCodes(orgId);

    const [revealLead, setRevealLead] = useState<number>(120);
    const [grace, setGrace] = useState<number>(30);
    // null = no push. Empty input clears it (off); 0 is a valid "at start" value.
    const [notifyLead, setNotifyLead] = useState<number | null>(null);
    const [rows, setRows] = useState<DraftRow[]>([]);
    // Track which org the draft was hydrated from. Switching orgs must wipe
    // rows — otherwise saving sends the previous org's court IDs to the new
    // org's endpoint and the batch rejects them all with "court not found".
    const [hydratedFor, setHydratedFor] = useState<string | null>(null);

    // Clear draft state the moment orgId changes, so nothing stale can be
    // submitted while the new org's data is still loading.
    useEffect(() => {
        setHydratedFor(null);
        setRows([]);
    }, [orgId]);

    // Hydrate once per org, then let user edits take over within that org.
    useEffect(() => {
        if (!data || !orgId || hydratedFor === orgId) return;
        setRevealLead(data.accessCodeRevealLeadMinutes);
        setGrace(data.accessCodeGraceMinutes);
        setNotifyLead(data.accessCodeNotifyLeadMinutes);
        setRows(data.courts.map((c) => ({ courtId: c.courtId, code: c.code ?? '' })));
        setHydratedFor(orgId);
    }, [data, orgId, hydratedFor]);

    const serverByCourtId = useMemo(() => {
        const map = new Map<number, { code: string | null; updatedAt: string | null; updatedByEmail: string | null; courtName: string }>();
        if (data) {
            for (const c of data.courts) map.set(c.courtId, c);
        }
        return map;
    }, [data]);

    const allValid = rows.every((r) => r.code === '' || /^\d{4}$/.test(r.code));

    const hasChanges = useMemo(() => {
        if (!data) return false;
        if (revealLead !== data.accessCodeRevealLeadMinutes) return true;
        if (grace !== data.accessCodeGraceMinutes) return true;
        if (notifyLead !== data.accessCodeNotifyLeadMinutes) return true;
        for (const r of rows) {
            const server = serverByCourtId.get(r.courtId)?.code ?? '';
            if ((r.code || '') !== (server || '')) return true;
        }
        return false;
    }, [data, revealLead, grace, notifyLead, rows, serverByCourtId]);

    const handleCodeChange = (courtId: number, value: string) => {
        const sanitized = value.replace(/\D/g, '').slice(0, 4);
        setRows((prev) => prev.map((r) => (r.courtId === courtId ? { ...r, code: sanitized } : r)));
    };

    const handleRandomize = (courtId: number) => {
        const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        setRows((prev) => prev.map((r) => (r.courtId === courtId ? { ...r, code: random } : r)));
    };

    const handleSaveAll = async () => {
        if (!orgId) return;
        const input: BatchUpdateAccessCodesInput = {
            accessCodeRevealLeadMinutes: revealLead,
            accessCodeGraceMinutes: grace,
            accessCodeNotifyLeadMinutes: notifyLead,
            courts: rows.map((r) => ({
                courtId: r.courtId,
                code: r.code === '' ? null : r.code,
            })),
        };
        try {
            await save(input);
            toast({
                title: translate('access_codes.saved'),
                status: 'success',
                duration: 3000,
                position: 'top-right',
            });
        } catch (err: any) {
            toast({
                title: translate('error'),
                description: err?.message,
                status: 'error',
                duration: 4000,
                position: 'top-right',
            });
        }
    };

    if (!orgId) {
        return (
            <PageCard title={translate('access_codes.title')}>
                <Box px={{ base: 4, md: 6 }} pb={6}>
                    <Text>{translate('access_codes.no_organization')}</Text>
                </Box>
            </PageCard>
        );
    }

    if (!featureEnabled) {
        return (
            <PageCard title={translate('access_codes.title')}>
                <Box px={{ base: 4, md: 6 }} pb={6}>
                    <Text>{translate('access_codes.feature_disabled')}</Text>
                </Box>
            </PageCard>
        );
    }

    return (
        <PageCard title={translate('access_codes.title')}>
            <VStack align="stretch" spacing={6} px={{ base: 4, md: 6 }} pb={6}>
                <Text color={labelColor} fontSize="sm">
                    {translate('access_codes.intro')}
                </Text>

                <HStack align="flex-start" spacing={6}>
                    <FormControl maxW="260px">
                        <FormLabel>{translate('access_codes.reveal_lead_label')}</FormLabel>
                        <NumberInput
                            value={revealLead}
                            min={0}
                            max={1440}
                            onChange={(_, v) => setRevealLead(Number.isFinite(v) ? v : 0)}
                        >
                            <NumberInputField />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                            {translate('access_codes.reveal_lead_hint')}
                        </Text>
                    </FormControl>

                    <FormControl maxW="260px">
                        <FormLabel>{translate('access_codes.grace_label')}</FormLabel>
                        <NumberInput
                            value={grace}
                            min={0}
                            max={240}
                            onChange={(_, v) => setGrace(Number.isFinite(v) ? v : 0)}
                        >
                            <NumberInputField />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                            {translate('access_codes.grace_hint')}
                        </Text>
                    </FormControl>

                    <FormControl maxW="260px">
                        <FormLabel>{translate('access_codes.notify_lead_label')}</FormLabel>
                        <NumberInput
                            value={notifyLead ?? ''}
                            min={0}
                            max={1440}
                            onChange={(valueAsString, valueAsNumber) =>
                                setNotifyLead(
                                    valueAsString === ''
                                        ? null
                                        : Number.isFinite(valueAsNumber)
                                          ? valueAsNumber
                                          : null,
                                )
                            }
                        >
                            <NumberInputField />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                            {translate('access_codes.notify_lead_hint')}
                        </Text>
                    </FormControl>
                </HStack>

                <Box>
                    {readState === 'loading' ? (
                        <HStack><Spinner size="sm" /><Text>{translate('loading')}</Text></HStack>
                    ) : readState === 'unreadable' ? (
                        <InlineErrorState message={translate(READ_FAILED_I18N_KEY as any)} />
                    ) : readState === 'never-issued' ? (
                        // Unreachable from this page today — both of the hook's
                        // gate terms (`enabled && !!organizationId`) are
                        // re-derived by the two early returns above, so a
                        // never-issued read cannot reach this branch. It is
                        // written anyway because that equivalence is D62's
                        // fragile pattern: it lives in a different expression
                        // from the gate, and the day a third term is added to
                        // `useGetAccessCodes` the guard silently stops covering
                        // it and this page spins forever. Honesty and a budget
                        // entry cleared, not a fix — labelled as such, per the
                        // run-114 precedent for the same situation.
                        <ScopePrompt gate="organization" />
                    ) : rows.length === 0 ? (
                        <Text>{translate('access_codes.no_courts')}</Text>
                    ) : (
                        <Table variant="simple" size="sm" maxW="720px">
                            <Thead>
                                <Tr>
                                    <Th width="40%">{translate('access_codes.court')}</Th>
                                    <Th width="140px">{translate('access_codes.code')}</Th>
                                    <Th>{translate('access_codes.last_updated')}</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {rows.map((row) => {
                                    const server = serverByCourtId.get(row.courtId);
                                    const invalid = row.code !== '' && !/^\d{4}$/.test(row.code);
                                    const updatedLabel = server?.updatedAt
                                        ? new Date(server.updatedAt).toLocaleString()
                                        : '—';
                                    return (
                                        <Tr key={row.courtId}>
                                            <Td>{server?.courtName ?? `Court ${row.courtId}`}</Td>
                                            <Td>
                                                <HStack spacing={2}>
                                                    <Input
                                                        value={row.code}
                                                        onChange={(e) => handleCodeChange(row.courtId, e.target.value)}
                                                        placeholder="####"
                                                        maxLength={4}
                                                        width="100px"
                                                        isInvalid={invalid}
                                                    />
                                                    <Tooltip label={translate('access_codes.randomize')}>
                                                        <IconButton
                                                            aria-label={translate('access_codes.randomize')}
                                                            icon={<MdRefresh />}
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => handleRandomize(row.courtId)}
                                                        />
                                                    </Tooltip>
                                                </HStack>
                                            </Td>
                                            <Td><Text fontSize="xs" color="gray.500">{updatedLabel}</Text></Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    )}
                </Box>

                <HStack justifyContent="flex-end">
                    <Button
                        colorScheme="blue"
                        onClick={handleSaveAll}
                        isDisabled={!hasChanges || !allValid || isSaving}
                        isLoading={isSaving}
                    >
                        {translate('access_codes.save_all')}
                    </Button>
                </HStack>
            </VStack>
        </PageCard>
    );
}
